import { DocumentNode, gql } from '@apollo/client';
import { Image, LinkBox, LinkOverlay, Stack, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import NextLink from 'next/link';
import * as React from 'react';

import { EventScheduleInfo } from '@/components/EventScheduleInfo';
import { EventStuff } from '@/components/EventStuff';
import { getAppropriateEventLink } from '@/hooks/getAppropriateEventLink';
import { Card, CardProps } from '@/imports/ui/chakra/components/Card';
import { ImageFallbackMavericks } from '@/imports/ui/chakra/components/ImageFallbackMavericks';

import { EventListItem_EventFragment } from './graphql-types/EventListItem_EventFragment';

export type EventListItemProps = {
  event: EventListItem_EventFragment;
  shadow?: CardProps['shadow'];

  // for amplitude tracking purposes
  trackClickEvent?: () => void;
};

export const EventListItem: React.FC<EventListItemProps> & {
  eventFragment: DocumentNode;
} = (props) => {
  const isEventPast = dayjs().isAfter(props.event.endDate);
  const link = getAppropriateEventLink(props.event);

  return (
    <LinkBox>
      <Card shadow={props.shadow}>
        <Image
          borderTopRadius="xl"
          w="full"
          src={props.event.bannerImage?.imageProxy || undefined}
          alt={`${props.event.name} banner`}
          fallback={
            <ImageFallbackMavericks
              borderTopRadius="xl"
              title={props.event.name}
            />
          }
        />
        <Stack p="4" spacing="4">
          <Stack spacing="1">
            <LinkOverlay
              as={NextLink}
              href={link}
              onClick={props.trackClickEvent}
            >
              <Text textStyle="xlBodyStrong">{props.event.name}</Text>
            </LinkOverlay>

            <EventScheduleInfo
              event={props.event}
              showAddToCalendar={!isEventPast}
            />
          </Stack>

          <EventStuff
            event={props.event}
            allowSpecialTooltip={false}
            allowConferenceJoin={true}
          />
        </Stack>
      </Card>
    </LinkBox>
  );
};

EventListItem.eventFragment = gql`
  fragment EventListItem_EventFragment on EventV2 {
    _id
    endDate
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
    name
    ...GetAppropriateEventLink_EventFragment
    ...EventStuff_EventFragment
    ...EventScheduleInfo_EventFragment
  }
  ${getAppropriateEventLink.eventFragment}
  ${EventStuff.eventFragment}
  ${EventScheduleInfo.eventFragment}
`;
